import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, ArrowUpRight, MapPin } from "lucide-react";

/* Countries we cover in phase 1 — kept small so the filter chips fit inline. */
const COUNTRY_TAG_STYLE = {
  France: "bg-[color:var(--moe-elec)] text-white",
  Germany: "bg-[color:var(--moe-yolk)] text-ink",
  Italy: "bg-[color:var(--moe-pink)] text-ink",
  Spain: "bg-[color:var(--moe-mint)] text-ink",
  Belgium: "bg-[color:var(--moe-cyan)] text-ink",
  "United Kingdom": "bg-ink text-paper",
  Netherlands: "bg-[color:var(--moe-yolk)] text-ink",
  Ireland: "bg-[color:var(--moe-mint)] text-ink",
  Norway: "bg-[color:var(--moe-cyan)] text-ink",
  Sweden: "bg-[color:var(--moe-pink)] text-ink",
  Denmark: "bg-[color:var(--moe-yolk)] text-ink",
  Finland: "bg-[color:var(--moe-cyan)] text-ink",
  Switzerland: "bg-[color:var(--moe-elec)] text-white",
  Austria: "bg-[color:var(--moe-pink)] text-ink",
  Poland: "bg-[color:var(--moe-mint)] text-ink",
  "Czech Republic": "bg-[color:var(--moe-yolk)] text-ink",
  Hungary: "bg-[color:var(--moe-cyan)] text-ink",
};

const SORTS = {
  alpha:      { label: "Alphabetical",          fn: (a, b) => a.name.localeCompare(b.name) },
  partners:   { label: "Most Indian partners",  fn: (a, b) => b.partner_count - a.partner_count || a.name.localeCompare(b.name) },
  country:    { label: "By country (A→Z)",      fn: (a, b) => a.country.localeCompare(b.country) || a.name.localeCompare(b.name) },
};

export default function Universities() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [sort, setSort] = useState("partners");

  useEffect(() => {
    api.get("/partner-universities")
      .then((r) => setRows(r.data.partner_universities))
      .catch(() => setRows([]));
  }, []);

  const countries = useMemo(
    () => rows ? Array.from(new Set(rows.map((r) => r.country))).sort() : [],
    [rows],
  );

  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = q.trim().toLowerCase();
    let arr = rows.slice();
    if (s) arr = arr.filter((r) => r.name.toLowerCase().includes(s) || (r.city || "").toLowerCase().includes(s));
    if (country) arr = arr.filter((r) => r.country === country);
    arr.sort(SORTS[sort].fn);
    return arr;
  }, [rows, q, country, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 md:pt-20 pb-24">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-muted font-bold">Partner directory · Europe</div>
        <h1 className="mt-3 text-5xl md:text-6xl lg:text-7xl font-bold tracking-[-0.04em] leading-[0.95]">
          {rows ? rows.length : ""}<span className="mx-1"> </span>partner universities.
        </h1>
        <p className="mt-5 text-[17px] text-muted">
          The full set of European institutions your Indian home school has an active MoU with. Sourced from each B-school's own international office.
        </p>
      </div>

      {/* Filter bar. Search + country chip select + sort */}
      <div className="mt-12 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        <div className="relative md:flex-1 md:max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/60 pointer-events-none" />
          <input
            data-testid="universities-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by university or city"
            className="w-full h-12 pl-11 pr-4 text-[15px] text-ink placeholder-ink/45 bg-paper border border-ink rounded-full focus:outline-none focus:ring-2 focus:ring-[color:var(--moe-elec)]/25"
          />
        </div>
        <div className="relative">
          <select
            data-testid="universities-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="h-12 pl-4 pr-10 text-[15px] font-semibold text-ink bg-paper border border-ink rounded-full appearance-none focus:outline-none focus:ring-2 focus:ring-[color:var(--moe-elec)]/25"
          >
            <option value="">All countries</option>
            {countries.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
          <span aria-hidden="true" className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/70 text-xs">▾</span>
        </div>
        <div className="relative">
          <select
            data-testid="universities-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-12 pl-4 pr-10 text-[15px] font-semibold text-ink bg-paper border border-ink rounded-full appearance-none focus:outline-none focus:ring-2 focus:ring-[color:var(--moe-elec)]/25"
          >
            {Object.entries(SORTS).map(([k, s]) => (<option key={k} value={k}>Sort: {s.label}</option>))}
          </select>
          <span aria-hidden="true" className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/70 text-xs">▾</span>
        </div>
      </div>

      {!filtered ? (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 9 }).map((_, i) => <div key={i} className="moe-shimmer h-32" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-16 moe-card p-10 text-center">
          <div className="text-lg font-bold">Nothing matches those filters.</div>
          <div className="mt-1 text-sm text-muted">Try clearing the country filter or searching by city name.</div>
        </div>
      ) : (
        <>
          <div className="mt-6 text-xs text-muted">
            Showing <span className="font-bold text-ink">{filtered.length}</span> of {rows.length}
          </div>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((r) => (
              <div key={r.name} data-testid={`partner-uni-${r.name}`} className="moe-card p-5 md:p-6 group cursor-default">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[17px] md:text-[19px] font-bold text-ink leading-tight break-words">{r.name}</div>
                    {r.city && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted">
                        <MapPin size={12} /> {r.city}
                      </div>
                    )}
                  </div>
                  <span
                    className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-ink ${COUNTRY_TAG_STYLE[r.country] || "bg-paper text-ink"}`}
                  >
                    {r.country}
                  </span>
                </div>
                <div className="mt-4 pt-4 border-t border-ink/10 flex items-center justify-between text-xs">
                  <span className="text-muted">
                    <span className="font-bold text-ink">{r.partner_count}</span> Indian B-school MoU{r.partner_count === 1 ? "" : "s"}
                  </span>
                  <Link
                    to={`/countries/${encodeURIComponent(r.country)}`}
                    className="inline-flex items-center gap-1 font-semibold text-elec hover:underline underline-offset-4"
                  >
                    See {r.country} visa <ArrowUpRight size={12} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
